import { useEffect, useState } from "react";
import axios from "axios";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Slider } from "./ui/slider";
import { Briefcase, Loader2, Lock, Calculator, Send, Copy, Printer, CheckCircle2, Minus, Plus, AlertTriangle, Wrench } from "lucide-react";
import RoofMap from "./RoofMap";
import EvBenefitEditor from "./EvBenefitEditor";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const reteHttp = () => axios.create({ baseURL: API_BASE, headers: { Authorization: `Bearer ${localStorage.getItem("rete_session")}` } });

const eur = (n) => (n == null ? "—" : Number(n).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }));

export function PartnerQuote({ selfToken, preAuthed = false, prefill = null, onDone = null, rete = null }) {
  const [open, setOpen] = useState(preAuthed || !!rete);
  const [authed, setAuthed] = useState(preAuthed || !!rete);
  const http = rete ? reteHttp() : api;
  const quoteUrl = rete ? `/rete/c/${rete}/quote` : `/partner/self/${selfToken}/quote`;
  const emailUrl = rete ? `/rete/c/${rete}/send-proposal-email` : `/partner/self/${selfToken}/send-proposal-email`;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const [cfg, setCfg] = useState({ accumulo: true, wallbox: false, finanziamento: false, n_pannelli: "", n_batterie: "", pannello_colore: "standard", colore_potenza_wp: "", acconto: "", durata_mesi: "60", man_attiva: false, man_att: [], man_incluso: false, man_anni: 1, man_mod: "annuo", man_pa: "", man_pk: "", ...(prefill || {}) });
  const [factor, setFactor] = useState(1); // 1 = consigliato, 0 = minimo
  const [priceInput, setPriceInput] = useState("");
  const [manualTarget, setManualTarget] = useState(null); // prezzo IVA incl. pinnato manualmente
  const [compl, setCompl] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [result, setResult] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [manCfg, setManCfg] = useState(null);

  useEffect(() => {
    if (!authed) return;
    if (rete) {
      http.get(`/rete/c/${rete}/quote-config`).then((r) => {
        setColorList(r.data.colori?.list || []);
        setManCfg(r.data.manutenzione || {});
        setCfg((c) => ({ ...c, man_attiva: !!(r.data.manutenzione || {}).attiva_default, man_att: ((r.data.manutenzione || {}).attivita || []).filter((a) => a.active).map((a) => a.key), man_mod: (r.data.manutenzione || {}).modalita || "annuo" }));
        if (r.data.outlook_url) setResult({ url: r.data.outlook_url, print_url: `${r.data.outlook_url}/print`, email_to: r.data.email_to });
        if (r.data.email_sent) setEmailSent(true);
      }).catch((e) => { setManCfg({}); toast.error(formatApiError(e.response?.data?.detail) || "Accesso rete non valido"); });
      return;
    }
    api.get("/settings/colori-pannello").then((r) => setColorList(r.data.list || [])).catch(() => setColorList([
      { key: "standard", label: "Colorazione standard" }, { key: "total_black", label: "Total Black" }, { key: "ecored", label: "EcoRed" },
    ]));
    api.get("/settings/manutenzione").then((r) => {
      setManCfg(r.data);
      setCfg((c) => ({ ...c, man_attiva: !!r.data.attiva_default, man_att: (r.data.attivita || []).filter((a) => a.active).map((a) => a.key), man_mod: r.data.modalita || "annuo" }));
    }).catch(() => setManCfg({}));
  }, [authed]);

  // Calcolo baseline DOPO il caricamento della config manutenzione, così la proposta di valore
  // (Manutenzione Programmata) risulta attiva automaticamente come per gli altri utenti.
  useEffect(() => {
    if (authed && manCfg && !quote) calc();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, manCfg]);

  const doLogin = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.user?.role !== "partner_commerciale") {
        localStorage.removeItem("token");
        setErr("Queste credenziali non sono di un partner commerciale.");
        return;
      }
      if (data.access_token) localStorage.setItem("token", data.access_token);
      setAuthed(true);
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || "Accesso non riuscito");
    } finally {
      setLoading(false);
    }
  };

  const payload = (overrides = {}) => ({
    accumulo: cfg.accumulo,
    wallbox: cfg.wallbox,
    finanziamento: cfg.finanziamento,
    acconto: cfg.acconto !== "" ? parseFloat(cfg.acconto) : null,
    durata_mesi: cfg.durata_mesi ? parseInt(cfg.durata_mesi, 10) : null,
    n_pannelli: cfg.n_pannelli ? parseInt(cfg.n_pannelli, 10) : null,
    n_batterie: cfg.n_batterie ? parseInt(cfg.n_batterie, 10) : null,
    pannello_colore: cfg.pannello_colore || "standard",
    prezzo_factor: factor,
    prezzo_target_iva: manualTarget,
    complicazioni: compl,
    manutenzione_attiva: cfg.man_attiva,
    manutenzione_attivita: cfg.man_att,
    manutenzione_incluso: cfg.man_incluso,
    manutenzione_anni: cfg.man_anni ? parseInt(cfg.man_anni, 10) : 1,
    manutenzione_modalita: cfg.man_mod || "annuo",
    manutenzione_prezzo_annuo: cfg.man_pa !== "" && cfg.man_pa != null ? parseFloat(cfg.man_pa) : null,
    manutenzione_prezzo_kwp: cfg.man_pk !== "" && cfg.man_pk != null ? parseFloat(cfg.man_pk) : null,
    ...overrides,
  });

  const calc = async (overrides = {}) => {
    setBusy(true); setResult(null);
    try {
      const { data } = await http.post(quoteUrl, { ...payload(overrides), finalize: false });
      setQuote(data);
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Calcolo non riuscito");
    } finally { setBusy(false); }
  };

  const finalize = async () => {
    setBusy(true); setEmailSent(false);
    try {
      const { data } = await http.post(quoteUrl, { ...payload(), finalize: true });
      setResult(data); setQuote(data);
      toast.success(onDone ? "Preventivo aggiornato." : "Proposta generata. Ora puoi inviarla al cliente.");
      if (onDone) onDone(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Generazione non riuscita");
    } finally { setBusy(false); }
  };

  const sendEmail = async () => {
    setSending(true);
    try {
      const { data } = await http.post(emailUrl);
      setEmailSent(true);
      toast.success(`Proposta inviata al cliente${data?.email_to ? " (" + data.email_to + ")" : ""}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Invio email non riuscito");
    } finally { setSending(false); }
  };

  const stepPannelli = (delta) => {
    const base = cfg.n_pannelli !== "" ? parseInt(cfg.n_pannelli, 10) : (quote?.n_pannelli || 0);
    const val = Math.max(1, base + delta);
    setCfg((c) => ({ ...c, n_pannelli: String(val) }));
    if (quote) calc({ n_pannelli: val });
  };
  const stepBatterie = (delta) => {
    const base = cfg.n_batterie !== "" ? parseInt(cfg.n_batterie, 10) : (quote?.n_batterie || 0);
    const val = Math.max(0, base + delta);
    setCfg((c) => ({ ...c, n_batterie: String(val) }));
    if (quote) calc({ n_batterie: val });
  };

  const onFactorCommit = (vals) => {
    const f = (vals[0] ?? 100) / 100;
    setFactor(f);
    setManualTarget(null);
    if (quote) calc({ prezzo_factor: f, prezzo_target_iva: null });
  };

  // Sync del campo prezzo manuale quando cambia il preventivo calcolato
  useEffect(() => {
    if (quote?.prezzo_iva_incluso != null) setPriceInput(String(Math.round(quote.prezzo_iva_incluso)));
  }, [quote?.prezzo_iva_incluso]);

  const applyManualPrice = async () => {
    if (!quote) return;
    const min = quote.prezzo_min_iva, max = quote.prezzo_consigliato_iva;
    if (min == null || max == null || max <= min) return;
    let v = parseFloat(String(priceInput).replace(/[^0-9.,]/g, "").replace(/\./g, "").replace(",", "."));
    if (isNaN(v)) { setPriceInput(String(Math.round(quote.prezzo_iva_incluso))); return; }
    const clamped = Math.min(max, Math.max(min, v));
    if (Math.round(clamped) !== Math.round(v)) {
      toast.info(`Prezzo riportato entro i limiti consentiti (${eur(min)} – ${eur(max)})`);
    }
    const data = await calc({ prezzo_target_iva: clamped });
    if (data?.prezzo_factor != null) setFactor(data.prezzo_factor);
    setManualTarget(clamped);
  };

  const fullUrl = (path) => `${window.location.origin}${path}`;
  const showWp = cfg.pannello_colore === "total_black" || cfg.pannello_colore === "ecored";

  if (!open) {
    return (
      <Card className="rounded border-primary/30 bg-primary/5 p-5 mt-4 text-center" data-testid="partner-cta">
        <div className="flex items-center justify-center gap-2 text-primary font-semibold mb-1"><Briefcase className="h-4 w-4" /> Sei un partner commerciale?</div>
        <p className="text-xs text-muted-foreground mb-4 max-w-md mx-auto">Accedi con le tue credenziali per generare in autonomia il preventivo di questo cliente.</p>
        <Button variant="outline" onClick={() => setOpen(true)} data-testid="partner-open-btn"><Lock className="h-4 w-4 mr-1.5" /> Accedi come partner</Button>
      </Card>
    );
  }

  return (
    <Card className="rounded border-primary/40 bg-card p-5 mt-4" data-testid="partner-panel">
      <div className="flex items-center gap-2 text-primary font-semibold mb-4"><Briefcase className="h-4 w-4" /> {rete ? "Prezzo e proposta per il cliente (rete)" : "Area partner commerciale"}</div>

      {!authed ? (
        <form onSubmit={doLogin} className="space-y-3 max-w-sm">
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Email</Label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="partner-email" /></div>
          <div className="space-y-1.5"><Label className="text-xs uppercase tracking-widest text-muted-foreground">Password</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="partner-password" /></div>
          {err && <p className="text-sm text-destructive" data-testid="partner-error">{err}</p>}
          <Button type="submit" disabled={loading} data-testid="partner-login-btn" className="font-semibold">
            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Accedi
          </Button>
        </form>
      ) : (
        <div className="space-y-5">
          <div className="grid gap-3">
            <ToggleRow label="Accumulo (batteria)" checked={cfg.accumulo} onChange={(v) => { setCfg((c) => ({ ...c, accumulo: v })); if (quote) calc({ accumulo: v }); }} testid="partner-accumulo" />
            <ToggleRow label="Stazione di ricarica (wallbox)" checked={cfg.wallbox} onChange={(v) => { setCfg((c) => ({ ...c, wallbox: v })); if (quote) calc({ wallbox: v }); }} testid="partner-wallbox" />
            {!rete && <ToggleRow label="Finanziamento" checked={cfg.finanziamento} onChange={(v) => { setCfg((c) => ({ ...c, finanziamento: v })); if (quote) calc({ finanziamento: v }); }} testid="partner-finanziamento" />}
            <ToggleRow label="Manutenzione programmata" checked={cfg.man_attiva} onChange={(v) => { setCfg((c) => ({ ...c, man_attiva: v })); if (quote) calc({ manutenzione_attiva: v }); }} testid="partner-manutenzione" />
            {cfg.man_attiva && (manCfg?.attivita || []).filter((a) => a.active).map((a) => (
              <label key={a.key} className="flex items-center gap-2 text-xs pl-3 py-0.5 cursor-pointer" data-testid={`partner-man-att-${a.key}`}>
                <input type="checkbox" checked={cfg.man_att.includes(a.key)} onChange={(e) => { const na = e.target.checked ? [...cfg.man_att, a.key] : cfg.man_att.filter((k) => k !== a.key); setCfg((c) => ({ ...c, man_att: na })); if (quote) calc({ manutenzione_attivita: na }); }} />
                {a.label}
              </label>
            ))}
            {cfg.man_attiva && (
              <div className="rounded border border-amber-500/30 bg-amber-500/5 p-3 space-y-3" data-testid="partner-man-config">
                <ToggleRow label="Incluso nell'offerta" checked={!!cfg.man_incluso} onChange={(v) => { setCfg((c) => ({ ...c, man_incluso: v })); if (quote) calc({ manutenzione_incluso: v }); }} testid="partner-man-incluso" />
                {cfg.man_incluso && (
                  <div className="w-32"><Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Anni inclusi</Label><Input type="number" min="1" value={cfg.man_anni} onChange={(e) => setCfg((c) => ({ ...c, man_anni: e.target.value }))} onBlur={() => quote && calc()} data-testid="partner-man-anni" /></div>
                )}
                {quote?.manutenzione?.attiva ? (
                  <div className="rounded border border-primary/20 bg-primary/5 p-3 text-sm space-y-2" data-testid="partner-man-allinclusive">
                    <div className="text-[10px] uppercase tracking-widest text-primary font-semibold">Manutenzione Programmata All Inclusive</div>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
                      <div className="text-muted-foreground">Fascia applicata</div><div className="text-right font-medium">{quote.manutenzione.su_preventivo ? "Su preventivo" : (quote.manutenzione.fascia || "—")}</div>
                      <div className="text-muted-foreground">Canone base annuo</div><div className="text-right font-medium">{quote.manutenzione.su_preventivo ? "—" : eur(quote.manutenzione.canone_base)}</div>
                      {quote.manutenzione.trasferta && (
                        <>
                          <div className="text-muted-foreground">Km eccedenti (A/R)</div><div className="text-right font-medium">{quote.manutenzione.trasferta.km_eccedenti} km → {quote.manutenzione.trasferta.km_addebitabili} km</div>
                          <div className="text-muted-foreground">Costo trasferta</div><div className="text-right font-medium">{eur(quote.manutenzione.trasferta.costo)}</div>
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-between border-t border-border pt-2">
                      <span className="text-xs text-muted-foreground">Prezzo finale annuale (IVA 10% incl.)</span>
                      <span className="font-heading font-bold text-primary" data-testid="partner-man-prezzo-finale">{quote.manutenzione.su_preventivo ? "Su preventivo" : eur(quote.manutenzione.prezzo_finale_annuo_iva)}</span>
                    </div>
                    {quote.manutenzione.trasferta?.verifica_commerciale && <div className="text-[11px] text-amber-600">⚠ Verifica commerciale necessaria (costo trasferta al limite massimo)</div>}
                    {quote.manutenzione.incluso && <div className="text-[11px] text-muted-foreground">Incluso per {quote.manutenzione.anni || cfg.man_anni} anni: gratis per il cliente in quel periodo.</div>}
                  </div>
                ) : (
                  <p className="text-[11px] text-muted-foreground" data-testid="partner-man-hint">Il canone è calcolato automaticamente (fascia di potenza + trasferta). Premi "Calcola prezzo" per vederlo.</p>
                )}
              </div>
            )}

            {cfg.finanziamento && (
              <div className="rounded border border-border p-3 space-y-3" data-testid="partner-fin-config">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Finanziamento Findomestic · TAN 6,75%</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Acconto (€)</Label>
                    <Input type="number" min="0" step="100" placeholder="0" value={cfg.acconto}
                      onChange={(e) => setCfg((c) => ({ ...c, acconto: e.target.value }))}
                      onBlur={() => quote && calc()}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); quote && calc(); } }}
                      data-testid="partner-acconto" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">Numero rate (mesi)</Label>
                    <select value={cfg.durata_mesi}
                      onChange={(e) => { const val = e.target.value; setCfg((c) => ({ ...c, durata_mesi: val })); if (quote) calc({ durata_mesi: parseInt(val, 10) }); }}
                      data-testid="partner-durata"
                      className="w-full h-9 rounded border border-border bg-background px-3 text-sm">
                      {[36, 48, 60, 72, 84, 96, 108, 120].map((m) => <option key={m} value={m}>{m} rate</option>)}
                    </select>
                  </div>
                </div>
                {quote?.finanziamento && (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs pt-1 border-t border-border/60" data-testid="partner-fin-detail">
                    <RecapItem label="Importo finanziato" value={eur(quote.finanziamento.importo_finanziato)} />
                    <RecapItem label="Rata mensile" value={eur(quote.finanziamento.rata_mensile)} />
                    <RecapItem label="Durata" value={`${quote.finanziamento.durata_mesi || cfg.durata_mesi} rate`} />
                    <RecapItem label="Totale dovuto" value={eur(quote.finanziamento.totale_dovuto)} />
                    {quote.finanziamento.entro_soglia === false && (
                      <p className="col-span-2 text-[11px] text-amber-600 flex items-center gap-1"><AlertTriangle className="h-3 w-3" /> Importo oltre la soglia Findomestic (60.000 €).</p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Colorazione pannello */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Colorazione pannello</Label>
              <div className="grid grid-cols-3 gap-2 mt-1.5">
                {(colorList.length ? colorList : [{ key: "standard", label: "Standard" }]).map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => { setCfg((p) => ({ ...p, pannello_colore: c.key })); if (quote) calc({ pannello_colore: c.key }); }}
                    data-testid={`partner-colore-${c.key}`}
                    className={`rounded border px-2 py-2 text-xs font-medium transition-colors ${cfg.pannello_colore === c.key ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40"}`}
                  >
                    {c.image_url && <img src={c.image_url} alt={c.label} className="w-full h-12 object-cover rounded mb-1.5" data-testid={`partner-colore-img-${c.key}`} />}
                    {c.label}{(() => {
                      const delta = quote?.colori_turnkey_delta?.[c.key];
                      if (delta != null && delta > 0) return <span className="block text-[10px] font-normal opacity-70">+{eur(delta)}</span>;
                      if (delta === 0 || (delta == null && !c.surcharge_eur_kwp)) return <span className="block text-[10px] font-normal opacity-50">incluso</span>;
                      if (delta == null && c.surcharge_eur_kwp) return <span className="block text-[10px] font-normal opacity-50">calcola per il prezzo</span>;
                      return <span className="block text-[10px] font-normal opacity-50">incluso</span>;
                    })()}
                  </button>
                ))}
              </div>
              {showWp && (
                <div className="mt-2 flex items-start gap-1.5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-red-500" data-testid="partner-colore-alert">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span className="text-xs font-medium leading-snug">La potenza del singolo pannello potrebbe subire variazioni, a parità di qualità e tecnologia costruttiva.</span>
                </div>
              )}
            </div>

            {/* N pannelli / batterie con stepper +/- */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">N° pannelli</Label>
                <div className="flex items-center gap-1.5">
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => stepPannelli(-1)} data-testid="partner-pannelli-minus"><Minus className="h-4 w-4" /></Button>
                  <Input type="number" min="0" placeholder="auto" value={cfg.n_pannelli} onChange={(e) => setCfg((c) => ({ ...c, n_pannelli: e.target.value }))} onBlur={() => quote && calc()} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); quote && calc(); } }} data-testid="partner-n-pannelli" className="text-center" />
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => stepPannelli(1)} data-testid="partner-pannelli-plus"><Plus className="h-4 w-4" /></Button>
                </div>
                {quote?.n_pannelli != null && <p className="text-[10px] text-muted-foreground">Proposta: {quote.n_pannelli} moduli</p>}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">N° batterie</Label>
                <div className="flex items-center gap-1.5">
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" disabled={!cfg.accumulo} onClick={() => stepBatterie(-1)} data-testid="partner-batterie-minus"><Minus className="h-4 w-4" /></Button>
                  <Input type="number" min="0" placeholder="auto" value={cfg.n_batterie !== "" ? cfg.n_batterie : (quote?.n_batterie ?? "")} disabled={!cfg.accumulo} onChange={(e) => setCfg((c) => ({ ...c, n_batterie: e.target.value }))} onBlur={() => quote && calc()} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); quote && calc(); } }} data-testid="partner-n-batterie" className="text-center" />
                  <Button type="button" variant="outline" size="icon" className="h-9 w-9 shrink-0" disabled={!cfg.accumulo} onClick={() => stepBatterie(1)} data-testid="partner-batterie-plus"><Plus className="h-4 w-4" /></Button>
                </div>
                {cfg.accumulo && quote?.n_batterie != null && <p className="text-[10px] text-muted-foreground">Proposta: {quote.n_batterie} batterie</p>}
              </div>
            </div>
          </div>

          {/* Dichiarazione condizioni tecniche: se presente una, la preventivazione autonoma è bloccata */}
          <div className="rounded border border-border p-3" data-testid="partner-complicazioni">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Dichiarazione condizioni tecniche</div>
            <div className="space-y-2">
              {[["eternit", "Copertura in eternit/amianto"], ["terra", "Impianto a terra / campo a terra"], ["pensilina", "Pensilina / parcheggio fotovoltaico"]].map(([k, label]) => (
                <label key={k} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`partner-compl-${k}`}>
                  <input type="checkbox" checked={compl.includes(k)} onChange={(e) => setCompl((p) => (e.target.checked ? [...p, k] : p.filter((x) => x !== k)))} className="h-4 w-4 accent-primary" />
                  {label}
                </label>
              ))}
            </div>
            <p className="text-[11px] text-muted-foreground mt-2">Se presente una di queste condizioni, il progetto richiede la valutazione del team tecnico.</p>
          </div>

          {!rete && (<>
          <div className="space-y-2" data-testid="partner-roof-section">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Tetto e layout pannelli (opzionale)</div>
            <RoofMap lead={{ id: null, roof: null }} selfToken={selfToken} />
          </div>
          <EvBenefitEditor lead={{ id: null, cost_estimate: {} }} selfToken={selfToken} />
          </>)}

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => calc()} disabled={busy} data-testid="partner-calc-btn">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Calculator className="h-4 w-4 mr-1.5" />} Calcola prezzo</Button>
            <Button onClick={finalize} disabled={busy || !quote || quote?.blocked} data-testid="partner-finalize-btn"><Calculator className="h-4 w-4 mr-1.5" /> Genera proposta</Button>
          </div>

          {quote && quote.blocked && (
            <div className="rounded border border-amber-500/50 bg-amber-500/10 p-4 space-y-2" data-testid="partner-blocked">
              <div className="flex items-center gap-2 text-amber-600 font-semibold"><AlertTriangle className="h-4 w-4" /> Preventivazione non disponibile</div>
              <p className="text-sm text-foreground">{quote.block_message}</p>
              {(quote.block_reasons || []).length > 0 && (
                <ul className="text-xs text-muted-foreground list-disc pl-5">
                  {quote.block_reasons.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
              <p className="text-[11px] text-muted-foreground">Il lead resta salvato: il team tecnico Torri.Solar lo prenderà in carico.</p>
            </div>
          )}

          {quote && !quote.blocked && (
            <div className="rounded border border-border p-4 space-y-4" data-testid="partner-quote-result">
              {/* Riepilogo configurazione prima di generare la proposta */}
              <div className="rounded bg-secondary/40 border border-border p-3" data-testid="partner-config-recap">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Riepilogo configurazione</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-xs">
                  <RecapItem label="Colore pannello" value={(colorList.find((c) => c.key === (quote.pannello_colore || cfg.pannello_colore))?.label) || "Standard"} />
                  <RecapItem label="N° pannelli" value={quote.n_pannelli != null ? `${quote.n_pannelli}` : "—"} />
                  <RecapItem label="Potenza" value={`${quote.pv_kwp ?? "—"} kWp`} />
                  <RecapItem label="Accumulo" value={cfg.accumulo ? `${quote.batteria?.model ? quote.batteria.model + " · " : ""}${quote.batteria?.capacita_totale_kwh ?? quote.storage_kwh ?? 0} kWh${quote.batteria?.tipo ? ` (${quote.batteria.tipo})` : ""}${quote.n_batterie != null ? ` · ${quote.n_batterie} batt.` : ""}` : "No"} />
                  <RecapItem label="Stazione ricarica" value={cfg.wallbox ? `${quote.ricarica?.model || "Wallbox"}${quote.ricarica?.potenza_kw ? ` · ${quote.ricarica.potenza_kw} kW` : ""}` : "No"} />
                  <RecapItem label="Finanziamento" value={cfg.finanziamento ? `${quote.finanziamento?.durata_mesi || cfg.durata_mesi} rate${quote.finanziamento?.rata_mensile ? ` · ${eur(quote.finanziamento.rata_mensile)}/mese` : ""}` : "No"} />
                </div>
              </div>
              {/* Slider prezzo: Minimo ↔ Consigliato */}
              {quote.prezzo_min != null && quote.prezzo_consigliato != null && quote.prezzo_consigliato > quote.prezzo_min && (
                <div className="space-y-2" data-testid="partner-price-slider">
                  <div className="flex items-center justify-between text-[11px] uppercase tracking-widest text-muted-foreground">
                    <span>Prezzo minimo</span><span>Consigliato</span>
                  </div>
                  <Slider value={[Math.round((factor ?? 1) * 100)]} min={0} max={100} step={1} onValueChange={(v) => setFactor((v[0] ?? 100) / 100)} onValueCommit={onFactorCommit} data-testid="partner-price-range" />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{eur(quote.prezzo_min_iva)}</span>
                    <span className="font-semibold text-foreground">Scelto: {eur(quote.prezzo_iva_incluso)}</span>
                    <span>{eur(quote.prezzo_consigliato_iva)}</span>
                  </div>
                  <div className="pt-1">
                    <Label className="text-[11px] uppercase tracking-widest text-muted-foreground">Oppure inserisci il prezzo (IVA incl.)</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">€</span>
                        <Input
                          type="text"
                          inputMode="numeric"
                          className="pl-7"
                          value={priceInput}
                          onChange={(e) => setPriceInput(e.target.value)}
                          onBlur={applyManualPrice}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyManualPrice(); } }}
                          data-testid="partner-price-input"
                          placeholder={String(Math.round(quote.prezzo_consigliato_iva || 0))}
                        />
                      </div>
                      <Button variant="outline" size="sm" onClick={applyManualPrice} disabled={busy} data-testid="partner-price-apply">Applica</Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-1">Consentito tra {eur(quote.prezzo_min_iva)} e {eur(quote.prezzo_consigliato_iva)}. Valori fuori range vengono riportati al limite.</p>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <Stat label="Prezzo (IVA incl.)" value={eur(quote.prezzo_iva_incluso)} highlight />
                {quote.rata_mensile ? <Stat label="Rata mensile" value={eur(quote.rata_mensile)} /> : null}
                <Stat label="Potenza" value={`${quote.pv_kwp ?? "—"} kWp`} />
                {!rete && <Stat label="La tua provvigione" value={eur(quote.commission_eur)} sub={`${quote.commission_pct || 0}%`} highlight />}
              </div>
              <p className="text-[11px] text-muted-foreground">Prezzo definito dai parametri aziendali. I costi non sono visibili. Muovi lo slider per scegliere il prezzo dal minimo al consigliato.</p>
            </div>
          )}

          {result && (
            <div className="rounded border border-primary/40 bg-primary/5 p-4 space-y-3" data-testid="partner-final">
              <div className="flex items-center gap-2 text-primary font-semibold"><CheckCircle2 className="h-4 w-4" /> Proposta generata · link senza scadenza</div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={sendEmail} disabled={sending || emailSent || (rete && !result.email_to)} title={rete && !result.email_to ? "Il cliente non ha un indirizzo email" : undefined} data-testid="partner-send-email">{sending ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Send className="h-4 w-4 mr-1.5" />}{emailSent ? "Email inviata al cliente" : "Invia email al cliente"}</Button>
                <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(fullUrl(result.url)); toast.success("Link copiato"); }} data-testid="partner-copy-link"><Copy className="h-4 w-4 mr-1.5" /> Copia link</Button>
                <a href={result.url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" data-testid="partner-open-proposal">Apri proposta</Button></a>
                <a href={result.print_url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="outline" data-testid="partner-print"><Printer className="h-4 w-4 mr-1.5" /> Scarica PDF</Button></a>
              </div>
              {emailSent
                ? <p className="text-[11px] text-primary" data-testid="partner-email-sent-note">Proposta inviata via email a {result.email_to || "cliente"}.</p>
                : <p className="text-[11px] text-muted-foreground" data-testid="partner-email-pending-note">La proposta è generata ma <b>non ancora inviata</b>. Premi "Invia email al cliente" per spedirla.</p>}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

const ToggleRow = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between rounded border border-border px-3 py-2">
    <span className="text-sm">{label}</span>
    <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </div>
);

const Stat = ({ label, value, sub, highlight }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className={`font-heading font-bold ${highlight ? "text-primary text-lg" : "text-base"}`}>{value}{sub ? <span className="text-xs font-normal text-muted-foreground ml-1">{sub}</span> : null}</div>
  </div>
);

const RecapItem = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
    <div className="text-sm font-semibold text-foreground">{value}</div>
  </div>
);

export default PartnerQuote;
